import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, BookOpen, FileText, Video, ExternalLink, Folder, Compass,
  ListOrdered, Archive, LibraryBig, Sparkles, ChevronDown,
} from "lucide-react";
import { getVideoEmbed, paragraphs } from "./libraryUtils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_ICONS = {
  comecar: Sparkles,
  percursos: ListOrdered,
  autoeducacao: BookOpen,
  arquivo: Archive,
  todos: LibraryBig,
};

function KindIcon({ kind, className = "h-4 w-4" }) {
  if (kind === "video") return <Video className={className} />;
  if (kind === "itinerary") return <BookOpen className={className} />;
  return <FileText className={className} />;
}

function ItemCard({ item, index }) {
  const [open, setOpen] = useState(false);
  const embed = item.kind === "video" ? getVideoEmbed(item.url) : null;
  return (
    <article className="bg-white border border-[hsl(var(--border))] overflow-hidden" data-testid={`library-item-${item.id}`}>
      {embed && (
        <div className="aspect-video bg-black">
          <iframe
            src={embed} title={item.title} allowFullScreen
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            className="w-full h-full" loading="lazy"
          />
        </div>
      )}
      <div className="p-5 flex items-start gap-4">
        {typeof index === "number" && (
          <span className="display-heading text-2xl text-[hsl(var(--accent))] leading-none mt-0.5 shrink-0">
            {String(index + 1).padStart(2, "0")}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
            <KindIcon kind={item.kind} className="h-3.5 w-3.5 text-[hsl(var(--accent))]" />
            {item.kind === "video" ? "Vídeo" : item.kind === "itinerary" ? "Itinerário" : "Documento"}
          </div>
          <h3 className="mt-1.5 display-heading text-lg leading-tight">{item.title}</h3>
          {item.author && <p className="mt-0.5 text-xs text-[hsl(var(--accent))]">{item.author}</p>}
          {item.description && <p className="mt-2 text-xs text-[hsl(var(--muted-foreground))]">{item.description}</p>}
          {item.kind === "itinerary" && item.body && (
            <>
              <button
                onClick={() => setOpen(!open)}
                className="mt-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--primary))] hover:text-[#093630]"
                data-testid={`library-item-toggle-${item.id}`}
              >
                {open ? "Fechar" : "Ler"} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
              {open && (
                <div className="mt-3 space-y-3 text-sm text-[hsl(var(--foreground))] leading-relaxed">
                  {paragraphs(item.body).map((p, i) => <p key={i}>{p}</p>)}
                </div>
              )}
            </>
          )}
        </div>
        {item.url && item.kind !== "video" && (
          <a
            href={item.url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--primary))] hover:text-[#093630] shrink-0 mt-1"
            data-testid={`library-item-open-${item.id}`}
          >
            Abrir <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    </article>
  );
}

function ItemGrid({ items, numbered = false, emptyText }) {
  if (!items.length) {
    return (
      <div className="bg-white border border-[hsl(var(--border))] p-10 text-center">
        <p className="text-sm text-[hsl(var(--muted-foreground))]">{emptyText || "Ainda sem recursos nesta secção."}</p>
      </div>
    );
  }
  return (
    <div className="grid sm:grid-cols-2 gap-5">
      {items.map((it, i) => <ItemCard key={it.id} item={it} index={numbered ? i : undefined} />)}
    </div>
  );
}

export default function MemberLibrary() {
  const [items, setItems] = useState([]);
  const [paths, setPaths] = useState([]);
  const [cats, setCats] = useState({ thematic: [], institutional: [] });
  const [loading, setLoading] = useState(true);
  const [section, setSection] = useState("comecar");
  const [query, setQuery] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [ri, rp] = await Promise.all([
          axios.get(`${API}/members/library`, { withCredentials: true }),
          axios.get(`${API}/members/reading-paths`, { withCredentials: true }),
        ]);
        setItems(ri.data.items || []);
        setCats({
          thematic: ri.data.categories?.thematic || [],
          institutional: ri.data.categories?.institutional || [],
        });
        setPaths(rp.data.paths || []);
      } catch { toast.error("Erro a carregar a biblioteca."); } finally { setLoading(false); }
    })();
  }, []);

  const institutionalSlugs = useMemo(() => new Set(cats.institutional.map((c) => c.slug)), [cats]);

  const sections = useMemo(() => ([
    { key: "comecar", label: "Começar por aqui" },
    { key: "percursos", label: "Percursos de leitura" },
    { key: "autoeducacao", label: "Itinerários" },
    ...cats.thematic.map((c) => ({ key: c.slug, label: c.label })),
    { key: "arquivo", label: "Arquivo Institucional" },
    { key: "todos", label: "Todos os recursos" },
  ]), [cats]);

  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return null;
    const labelFor = (slug) => (cats.thematic.concat(cats.institutional).find((c) => c.slug === slug)?.label || slug || "");
    return items.filter((it) =>
      [it.title, it.author, it.description, labelFor(it.category), ...(it.tags || [])]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [query, items, cats]);

  const featured = useMemo(() => items.filter((i) => i.featured_start), [items]);
  const archiveGrouped = useMemo(() => {
    const inArchive = items.filter((i) => i.category && institutionalSlugs.has(i.category));
    const map = new Map();
    for (const it of inArchive) {
      if (!map.has(it.category)) map.set(it.category, []);
      map.get(it.category).push(it);
    }
    return cats.institutional.filter((c) => map.has(c.slug)).map((c) => ({ ...c, items: map.get(c.slug) }));
  }, [items, cats, institutionalSlugs]);

  const renderSection = () => {
    if (section === "comecar") {
      return (
        <div data-testid="library-section-comecar">
          <p className="text-sm text-[hsl(var(--muted-foreground))] max-w-2xl mb-6">
            Uma seleção essencial para quem chega à biblioteca: os recursos que recomendamos ler ou ver primeiro.
          </p>
          <ItemGrid items={featured} emptyText="A Direção está a preparar a seleção de entrada. Explore entretanto as categorias temáticas." />
        </div>
      );
    }
    if (section === "percursos") {
      return (
        <div className="space-y-10" data-testid="library-section-percursos">
          <p className="text-sm text-[hsl(var(--muted-foreground))] max-w-2xl">
            Sequências curadas de leitura e formação, ordenadas segundo o método: Vida → Evangelho → Igreja → Kentenich → decisão → missão.
          </p>
          {paths.length === 0 ? (
            <div className="bg-white border border-[hsl(var(--border))] p-10 text-center">
              <p className="text-sm text-[hsl(var(--muted-foreground))]">Ainda sem percursos de leitura publicados.</p>
            </div>
          ) : paths.map((p) => (
            <div key={p.id} data-testid={`reading-path-${p.id}`}>
              <div className="flex items-center gap-2 mb-1">
                <Compass className="h-4 w-4 text-[hsl(var(--accent))]" />
                <h2 className="display-heading text-2xl">{p.title}</h2>
              </div>
              {p.description && <p className="text-xs text-[hsl(var(--muted-foreground))] mb-4 max-w-2xl">{p.description}</p>}
              <div className="mt-4">
                <ItemGrid items={p.items || []} numbered emptyText="Percurso ainda sem recursos." />
              </div>
            </div>
          ))}
        </div>
      );
    }
    if (section === "autoeducacao") {
      const autoItems = items.filter((i) => i.kind === "itinerary");
      return (
        <div data-testid="library-section-autoeducacao">
          <p className="text-sm text-[hsl(var(--muted-foreground))] max-w-2xl mb-6">
            Os itinerários são os mapas do caminho de auto-educação — esboços de estudo pessoal que preparam
            e orientam os percursos. Para caminhar um percurso completo, com sessões presenciais e inscrição,
            visite a página{" "}
            <a href="/membros/percursos" className="text-[hsl(var(--primary))] underline" data-testid="library-link-percursos">Percursos</a>.
          </p>
          <ItemGrid items={autoItems} emptyText="Ainda sem itinerários publicados." />
        </div>
      );
    }
    if (section === "arquivo") {
      return (
        <div className="space-y-10" data-testid="library-section-arquivo">
          <p className="text-sm text-[hsl(var(--muted-foreground))] max-w-2xl">
            Documentos da fundação, atas, comunicações e demais material institucional do CIEES Portugal.
          </p>
          {archiveGrouped.length === 0 ? (
            <div className="bg-white border border-[hsl(var(--border))] p-10 text-center">
              <p className="text-sm text-[hsl(var(--muted-foreground))]">Ainda sem documentos institucionais publicados.</p>
            </div>
          ) : archiveGrouped.map((g) => (
            <div key={g.slug} data-testid={`archive-cat-${g.slug}`}>
              <div className="flex items-center gap-2 mb-4">
                <Folder className="h-4 w-4 text-[hsl(var(--accent))]" />
                <h2 className="display-heading text-2xl">{g.label}</h2>
              </div>
              <ItemGrid items={g.items} />
            </div>
          ))}
        </div>
      );
    }
    if (section === "todos") {
      return (
        <div data-testid="library-section-todos">
          <ItemGrid items={items} emptyText="Ainda sem recursos publicados." />
        </div>
      );
    }
    // thematic category
    const catItems = items.filter((i) => i.category === section);
    return (
      <div data-testid={`library-section-${section}`}>
        <ItemGrid items={catItems} />
      </div>
    );
  };

  return (
    <section className="container-page py-12 lg:py-16" data-testid="member-library-page">
      <div className="overline">Biblioteca</div>
      <h1 className="display-heading text-4xl sm:text-5xl mt-3">Conhecer, discernir, transformar.</h1>
      <span className="accent-rule mt-6 block" />
      <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))] max-w-2xl">
        Uma biblioteca para conhecer, discernir e transformar a vida.
      </p>

      {/* Search */}
      <div className="mt-8 relative max-w-xl">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-[hsl(var(--muted-foreground))]" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Pesquisar por título, autor, tema ou palavra-chave…"
          className="w-full bg-white border border-[hsl(var(--border))] pl-10 pr-4 py-3 text-sm focus:outline-none focus:border-[hsl(var(--accent))]"
          data-testid="library-search-input"
        />
      </div>

      {loading ? (
        <p className="mt-12 text-[hsl(var(--muted-foreground))]">A carregar…</p>
      ) : searchResults ? (
        <div className="mt-10" data-testid="library-search-results">
          <h2 className="display-heading text-2xl mb-6">
            {searchResults.length === 0
              ? `Sem resultados para "${query.trim()}"`
              : `${searchResults.length} resultado${searchResults.length === 1 ? "" : "s"} para "${query.trim()}"`}
          </h2>
          <ItemGrid items={searchResults} emptyText="Tente outra palavra-chave, autor ou tema." />
        </div>
      ) : (
        <div className="mt-10 grid lg:grid-cols-[240px_1fr] gap-10">
          {/* Section nav */}
          <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible -mx-1 px-1" data-testid="library-section-nav">
            {sections.map((s) => {
              const Icon = SECTION_ICONS[s.key] || Folder;
              const active = section === s.key;
              return (
                <button
                  key={s.key}
                  onClick={() => setSection(s.key)}
                  className={`inline-flex items-center gap-2.5 px-3.5 py-2.5 text-left text-xs uppercase tracking-[0.14em] whitespace-nowrap lg:whitespace-normal border-l-2 transition-colors ${
                    active
                      ? "border-[hsl(var(--accent))] bg-white text-[hsl(var(--primary))] font-medium"
                      : "border-transparent text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-white/60"
                  }`}
                  data-testid={`library-nav-${s.key}`}
                >
                  <Icon className="h-3.5 w-3.5 shrink-0" /> {s.label}
                </button>
              );
            })}
          </nav>

          {/* Content */}
          <div className="min-w-0">
            <h2 className="display-heading text-3xl mb-6">
              {sections.find((s) => s.key === section)?.label}
            </h2>
            {renderSection()}
          </div>
        </div>
      )}
    </section>
  );
}
